import React, { useState } from 'react';
import {
  Typography,
  Avatar,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import './GuideVideoCard.css';

const getYoutubeId = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url?.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

const GuideVideoCard = ({ video, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const isYoutube = video.video_type === 'youtube';
  const youtubeId = isYoutube ? getYoutubeId(video.youtube_url) : null;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: video.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="video-card" {...attributes}>
      {/* MEDIA (ZONE DRAG UNIQUEMENT ICI) */}
      <div className="video-media" {...listeners}>
        <div className="video-badge video-badge--position">
          POS {video.position}
        </div>

        <div
          className={`video-badge video-badge--status ${video.is_active ? 'active' : 'inactive'}`}
        >
          <FiberManualRecordIcon sx={{ fontSize: 10 }} />
          {video.is_active ? 'ACTIF' : 'INACTIF'}
        </div>

        {isYoutube && youtubeId ? (
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${youtubeId}?rel=0`}
            style={{ border: 0 }}
            allowFullScreen
          />
        ) : (
          <video
            width="100%"
            height="100%"
            style={{ objectFit: 'cover' }}
            controls
          >
            <source
              src={`${process.env.REACT_APP_API_URL}/storage/${video.video_path}`}
            />
          </video>
        )}
      </div>

      {/* CONTENT */}
      <div className="video-content">
        <Stack direction="row" justifyContent="space-between">
          <Typography className="video-title">
            {video.title?.length > 40
              ? video.title.slice(0, 40) + '...'
              : video.title}
          </Typography>

          {/* ❌ PAS DE DRAG ICI (sinon bug menu) */}
          <IconButton
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Typography className="video-desc">
          {video.description?.length > 40
            ? video.description.slice(0, 40) + '...'
            : video.description}
        </Typography>

        <div className="video-footer">
          <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Avatar
                src={video.author_avatar}
                sx={{ width: 24, height: 24 }}
              />
              <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
                {video.author_name}
              </Typography>
              <CheckCircleIcon sx={{ fontSize: 13, color: '#4318FF' }} />
            </Stack>

            <Typography sx={{ fontSize: 10, color: '#A3AED0' }}>
              {video.views} vues
            </Typography>
          </Stack>
        </div>
      </div>

      {/* MENU */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onEdit(video);
          }}
        >
          <EditTwoToneIcon fontSize="small" /> Modifier
        </MenuItem>

        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onDelete(video.id);
          }}
        >
          <DeleteTwoToneIcon fontSize="small" /> Supprimer
        </MenuItem>
      </Menu>
    </div>
  );
};

export default GuideVideoCard;
